import { times } from 'lodash'
import { Node as ProseNode } from 'prosemirror-model'
import { findSplitPoints } from 'utils/arrayUtils'

/**
 * The following regex is used to determine which punctuation marks can split a sentence.
 * It is a combination of the following:
 * 1. One of the following:
 *   1.1. A question mark
 *   1.2. A period, as long as it is not followed by another period (i.e. an ellipsis)
 *   1.3. An exclamation point
 *   1.4. An ellipsis
 * 2. Which must be followed by one of:
 *  2.1. A space
 *  2.2. The end of the string
 */
export const splitRegex = /(?<splitChar>\?|(\.(?!\.))|!|\.\.\.)(\s|$)/

/**
 * Split a node "by sentence", that is, split that node into `N` nodes (where N = `chunks`) of the
 * same type and attributes as the original node, using a period as the delimiter, where each node
 * has roughly the same number of characters, balanced as evenly as possible (using the period as a
 * split point).
 * Preserves marks and attributes when splitting
 * @param node A prosemirror node
 * @param chunks The number of nodes to split the original node into
 * @returns An array of nodes, each of which is one "sentence" of the same type as the original node
 * and contains the same marks as that sentence contained in the original node
 */
export function splitNodeIntoSentences(node: ProseNode, chunks: number): ProseNode[] {
  if (!node.type.groups.includes('textWrap')) {
    throw new Error('Node is not a textWrap')
  }
  const punctuationStops: number[] = []
  // find all descendants of the node that are Text nodes
  node.descendants((child, pos) => {
    if (!child.isText) return
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let regexResult = splitRegex.exec(child.text!)
    if (regexResult?.index !== null) {
      let splitCharIdx = regexResult?.index
      // while there are still matches left
      while (splitCharIdx !== undefined) {
        // calculate the length of the matched character (we can't simply assume `1` because of ellipses)
        const splitCharLength = regexResult?.groups?.splitChar?.length ?? 1
        // add the position of the matched character to the array that holds the points at which we want to split the node
        punctuationStops.push(pos + splitCharIdx + splitCharLength - 1)
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        regexResult = splitRegex.exec(child.text!.slice(splitCharIdx + 1))
        const nextIndex = regexResult?.index
        // if there are no more matches, break out of the loop
        if (nextIndex === undefined) {
          splitCharIdx = undefined
          break
        }
        // set the index of the next match
        splitCharIdx += nextIndex + 1
      }
    }
  })
  if (
    chunks === 1 ||
    punctuationStops.length === 0 ||
    (punctuationStops.length === 1 && node.textContent.endsWith('.'))
  ) {
    // Return early if we don't need to split the node:
    // 1. If we only want one chunk
    // 2. If there are no periods in the node
    // 3. If there is only one period in the node, and it's at the end of the node
    return [node]
  }

  // convert the punctuationStops array into an array of sentence sizes
  // so we can pipe it into the findSplitPoints function
  const sentenceSizes = punctuationStops.map((stop, idx) => {
    if (idx === 0) return stop
    return stop - punctuationStops[idx - 1]
  })

  // find the points at which we want to split the node
  const splitPoints = (() => {
    const splitIndexes = findSplitPoints(sentenceSizes, chunks)
    const lastChunkIdx = chunks - 1

    // convert the splitIndexes into an array of `[start,end]` points
    // for example if we want to break a sentence at `10`, we want two chunks: `[0,10]` and `[11,end]`
    const splitPoints = times<[start: number, end: number]>(chunks, (idx) => {
      const previousSplitIndex = idx > 0 ? splitIndexes[idx - 1] : null
      const currentSplitIndex = idx < lastChunkIdx ? splitIndexes[idx] : null
      const previousPunctuationStop = previousSplitIndex !== null ? punctuationStops[previousSplitIndex - 1] : null
      const currentPunctuationStop = currentSplitIndex !== null ? punctuationStops[currentSplitIndex - 1] : null

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (idx === 0) return [0, currentPunctuationStop!]
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (idx === lastChunkIdx) return [previousPunctuationStop! + 1, node.textContent.length - 1]
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return [previousPunctuationStop! + 1, currentPunctuationStop!]
    })

    return splitPoints
  })()

  const sentenceNodes: ProseNode[] = []
  splitPoints.forEach(([start, end]) => {
    // we need to add 1 to the end index because the `cut` function is exclusive
    let sentenceNode = node.cut(start, end + 1)
    if (sentenceNode.textContent.startsWith(' ')) {
      // trim leading space.
      sentenceNode = sentenceNode.cut(1)
    }
    sentenceNodes.push(sentenceNode)
  })
  return sentenceNodes
}
